import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X, UploadCloud, Loader2, Check, FileText, Trash2, Sparkles,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Input, Select } from "./ui/Primitives";

/**
 * AI-powered FOOD-MENU PDF importer. Same two-step flow as
 * CocktailPdfImportModal, but talks to /api/imports/recipes/*.
 */
export default function RecipePdfImportModal({ onClose, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState("");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const fileRef = useRef();

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/recipes/pdf-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setFilename(data.filename || file.name);
      setItems((data.items || []).map((it, i) => ({ ...it, _idx: i, _keep: true })));
      setCategories(data.categories || []);
      if (!data.items?.length) {
        toast.error(t("imports.pdf_no_items", "Keine Gerichte erkannt."));
      } else {
        setStep("review");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const update = (idx, patch) => setItems((xs) => xs.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const remove = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));

  // Phase 29 F5: validation — confirm button disabled until all kept rows
  // have a non-empty name.
  const invalidIdx = items
    .map((x, i) => (x._keep && (!x.name || !x.name.trim()) ? i : -1))
    .filter((i) => i >= 0);
  const keptCount = items.filter((x) => x._keep).length;
  const canConfirm = invalidIdx.length === 0 && keptCount > 0;

  const confirm = async () => {
    const keep = items.filter((x) => x._keep && x.name && x.name.trim());
    if (!keep.length) {
      toast.error(t("imports.pdf_no_items_to_save", "Keine Gerichte zum Speichern."));
      return;
    }
    setBusy(true);
    try {
      const body = {
        filename,
        items: keep.map((x) => ({
          name: x.name.trim(),
          selling_price: x.selling_price ? Number(x.selling_price) : null,
          category: x.category || "Hauptgang",
          description: (x.description || "").trim(),
        })),
      };
      const { data } = await api.post("/imports/recipes/pdf-confirm", body);
      toast.success(t("imports.pdf_created_recipes", "{{count}} Gerichte als Entwurf gespeichert.", { count: data.created }));
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="bg-bg-void border-l border-white/10 w-full max-w-4xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="recipe-pdf-modal"
      >
        <header className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 z-10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-brand flex items-center gap-2">
              <Sparkles size={12} /> AI-Import
            </div>
            <h2 className="text-lg font-semibold">Speisekarte als PDF importieren</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </header>

        <div className="p-6 space-y-4">
          {step === "upload" && (
            <Card className="p-8 border-dashed border-2 border-white/15 flex flex-col items-center text-center gap-4" data-testid="recipe-pdf-dropzone">
              <UploadCloud size={36} className="text-brand" />
              <div>
                <h3 className="font-semibold mb-1">Speisekarte hochladen</h3>
                <p className="text-sm text-ink-secondary max-w-md">
                  F&B Intelligence extrahiert automatisch Name, Preis, Kategorie und Beschreibung. Sie können vor dem Speichern alles prüfen und korrigieren.
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="recipe-pdf-file-input"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="recipe-pdf-pick">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                PDF auswählen
              </Button>
              <p className="text-xs text-ink-muted">PDF mit Text-Layer, max. 8 MB. Bild-PDFs werden nicht unterstützt.</p>
            </Card>
          )}

          {step === "review" && (
            <>
              <div className="text-sm text-ink-secondary">
                {items.length} Gerichte aus „{filename}" extrahiert. Bitte prüfen und bei Bedarf anpassen.
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto max-h-[65vh]">
                  <table className="w-full text-sm" data-testid="recipe-pdf-rows-table">
                    <thead className="bg-white/[0.02] sticky top-0 z-10">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2 w-36">Kategorie</th>
                        <th className="px-3 py-2 w-24 text-right">Preis</th>
                        <th className="px-3 py-2">Beschreibung</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => {
                        const isInvalid = it._keep && (!it.name || !it.name.trim());
                        return (
                        <tr key={i} className={`border-t border-white/5 ${!it._keep ? "opacity-50" : ""} ${isInvalid ? "bg-err/5" : ""}`} data-testid={`recipe-pdf-row-${i}`}>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={it._keep}
                              onChange={(e) => update(i, { _keep: e.target.checked })}
                              data-testid={`recipe-pdf-keep-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input value={it.name} onChange={(e) => update(i, { name: e.target.value })} data-testid={`recipe-pdf-name-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <Select value={it.category} onChange={(e) => update(i, { category: e.target.value })}>
                              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              className="text-right num"
                              value={it.selling_price ?? ""}
                              onChange={(e) => update(i, { selling_price: e.target.value })}
                              data-testid={`recipe-pdf-price-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={it.description || ""}
                              onChange={(e) => update(i, { description: e.target.value })}
                              data-testid={`recipe-pdf-desc-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                              <Trash2 size={12} className="text-err" />
                            </Button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="sticky bottom-0 bg-bg-void/95 backdrop-blur border-t border-white/10 py-3 -mx-6 px-6 flex items-center justify-between">
                <span className="text-xs text-ink-muted">
                  {invalidIdx.length > 0 ? (
                    <span className="text-err" data-testid="recipe-pdf-invalid-count">
                      {invalidIdx.length} Zeile(n) ohne Name — bitte ergänzen oder entfernen.
                    </span>
                  ) : (
                    `${keptCount} Gerichte werden als Entwurf angelegt.`
                  )}
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { setStep("upload"); setItems([]); }}>
                    Abbrechen
                  </Button>
                  <Button onClick={confirm} disabled={busy || !canConfirm} data-testid="recipe-pdf-confirm">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Importieren
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
